import { useEffect, useState } from "react"
import ModalCustom from "components/ModalCustom"
import {
  KeyFailureReason,
  KeyFixProgress,
  KeyFixStatus,
  KeyHealthScope,
} from "types/keyHealth"
import {
  generateTRCCode,
  reportKeyHealthFailure,
} from "services/keyHealthService"

interface KeyHealthDetailModalProps {
  fixProgress: KeyFixProgress[]
  isAutoFixing: boolean
  scope: KeyHealthScope
  onClose: () => void
}

const SCOPE_LABELS: Record<
  KeyHealthScope,
  { singular: string; plural: string }
> = {
  global: { singular: "item", plural: "items" },
  projects: { singular: "project", plural: "projects" },
  projectDetail: { singular: "project", plural: "projects" },
  projectComponent: { singular: "component", plural: "components" },
  buildComponent: { singular: "build component", plural: "build components" },
  build: { singular: "build item", plural: "build items" },
}

const REASON_LABEL: Record<KeyFailureReason, string> = {
  MISSING: "Required key is missing on this device.",
  WRONG_FORMAT: "The key format is invalid, so access cannot be restored.",
  WRONG_VALUE: "The key on this device does not match the key on the system.",
  NO_SERVER_KEY:
    "This item is missing a required key in the system. Please contact the owner.",
  OUT_OF_SYNC:
    "The key on this device is out of sync with the key on the system. Please retry.",
}

const STATUS_COLOR: Record<KeyFixStatus, string> = {
  pending: "#9CA3AF",
  fixing: "#E87A16",
  success: "#16a34a",
  error: "#dc2626",
}

const STATUS_LABEL: Record<KeyFixStatus, string> = {
  pending: "Waiting...",
  fixing: "Fixing...",
  success: "Restored",
  error: "Failed",
}

const StatusIcon = ({ status }: { status: KeyFixStatus }) => {
  if (status === "fixing") {
    return (
      <>
        <style>{`@keyframes khSpin { to { transform: rotate(360deg); } }`}</style>
        <div
          style={{
            width: 18,
            height: 18,
            border: "2px solid #E4E4E4",
            borderTopColor: "#E87A16",
            borderRadius: "50%",
            animation: "khSpin 0.8s linear infinite",
            flexShrink: 0,
          }}
        />
      </>
    )
  }
  if (status === "success") {
    return (
      <span
        style={{
          color: "#16a34a",
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ✓
      </span>
    )
  }
  if (status === "error") {
    return (
      <span
        style={{
          color: "#dc2626",
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ✗
      </span>
    )
  }
  // pending
  return (
    <div
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: "#D1D5DB",
        flexShrink: 0,
      }}
    />
  )
}

export const KeyHealthDetailModal = ({
  fixProgress,
  isAutoFixing,
  scope,
  onClose,
}: KeyHealthDetailModalProps) => {
  const [errorCode, setErrorCode] = useState("")
  const [copied, setCopied] = useState(false)
  const hasError = fixProgress.some((p) => p.status === "error")
  const allDone =
    fixProgress.length > 0 &&
    fixProgress.every((p) => p.status === "success" || p.status === "error")
  const allSuccess = allDone && !hasError

  useEffect(() => {
    if (!allDone || !hasError || errorCode) return
    const code = generateTRCCode()
    setErrorCode(code)
    reportKeyHealthFailure(
      code,
      fixProgress
        .filter((p) => p.status === "error")
        .map(({ id, name, reason }) => ({ id, name, reason }))
    )
  }, [allDone, hasError, errorCode, fixProgress])

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  const handleCopy = () => {
    if (!errorCode) return
    navigator.clipboard.writeText(errorCode)
    setCopied(true)
  }

  const labels = SCOPE_LABELS[scope]
  const allLabel = labels.plural

  const modalLabel = isAutoFixing
    ? "Restoring access..."
    : allSuccess
    ? `All ${allLabel} restored`
    : allDone && hasError
    ? `Some ${allLabel} could not be restored`
    : `Checking ${allLabel}`

  return (
    <ModalCustom
      widthModal={480}
      label={modalLabel}
      styleHeaderModal={{ height: "auto", paddingTop: 16 }}
      handleChangeButton={() => onClose}
      bodyChildren={
        <div className="flex flex-col gap-y-4 pb-2">
          {/* Top description */}
          {!allDone && (
            <p className="text-[14px] text-[#555555] leading-[22px]">
              Restoring access automatically. This may take a few seconds...
            </p>
          )}
          {allSuccess && (
            <p className="text-[14px] text-[#16a34a] leading-[22px]">
              {`All ${allLabel} are now accessible. The issue has been resolved.`}
            </p>
          )}
          {allDone && hasError && (
            <div className="flex flex-col gap-y-3">
              <p className="text-[14px] text-[#555555] leading-[22px]">
                {`Some ${allLabel} could not be restored automatically. You can retry or contact support.`}
              </p>
              <div className="flex items-center gap-x-2 bg-[#F5F5F5] rounded-md px-3 py-2">
                <span className="text-[14px] font-mono font-semibold text-[#222222] flex-1">
                  Error code: {errorCode}
                </span>
                <button
                  className="text-[12px] text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                  onClick={handleCopy}
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
            </div>
          )}

          {/* Per-key progress list */}
          {fixProgress.length > 0 && (
            <div className="flex flex-col gap-y-2 max-h-[280px] overflow-y-auto">
              {fixProgress.map((k) => (
                <div
                  key={k.id}
                  className="flex items-center gap-x-3 rounded-md px-3 py-2"
                  style={{ background: "#F7F7F7", border: "1px solid #E4E4E4" }}
                >
                  {/* Status icon — fixed width so content doesn't shift */}
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <StatusIcon status={k.status} />
                  </div>

                  {/* Item name + reason */}
                  <div className="flex flex-col flex-1 min-w-0">
                    <span
                      className="text-[13px] font-semibold truncate"
                      style={{ color: "#222222" }}
                    >
                      {k.name || k.id}
                    </span>
                    {k.status === "error" && (
                      <span
                        className="text-[11px] leading-[16px] truncate"
                        style={{ color: "#dc2626" }}
                      >
                        {REASON_LABEL[k.reason]}
                      </span>
                    )}
                  </div>

                  {/* Status label */}
                  <span
                    className="text-[11px] font-semibold whitespace-nowrap"
                    style={{ color: STATUS_COLOR[k.status] }}
                  >
                    {STATUS_LABEL[k.status]}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Empty state — fix hasn't initialised yet */}
          {fixProgress.length === 0 && (
            <div className="flex items-center justify-center py-4">
              <p className="text-[13px] text-[#9CA3AF]">Preparing...</p>
            </div>
          )}
        </div>
      }
    />
  )
}
